/**
 * Trimming a finished recording BEFORE it is saved (Geri, 2026-10-26).
 *
 * Someone forgets to stop: the recording keeps going on the bus home. Before
 * Save, the start and the end can be cut back to where the real movement
 * happened. The cut part never reaches the server — the saved activity is
 * exactly what an earlier Finish (or a later Start) would have produced, so
 * the area, the GP and the Trust Score all see only the kept part (Geri's
 * decision, DECISIONS #122). Only the middle cannot be cut: what remains is
 * always one contiguous, really recorded piece.
 *
 * Pure functions, no I/O — the recorder hook and the screen both use them.
 */
import type { TracePoint } from '@/types';
import { anchoredTotal, type LapMark, type PauseSpan, type RecorderState, type TrimRange, type TrimSummary } from './recorder';

/** Can the user still trim this recording? Only finished and not yet saved. */
export function canTrim(state: RecorderState): boolean {
  return state.status === 'finished' && state.awaitingSave === true && state.points.length >= 2;
}

/** The range clamped to the points, or `null` when it keeps everything. */
export function normalizeTrim(points: readonly TracePoint[], range: TrimRange | null | undefined): TrimRange | null {
  if (range == null || points.length < 2) return null;
  const last = points.length - 1;
  const start = clampIndex(range.start, 0, last - 1);
  const end = clampIndex(range.end, start + 1, last);
  return start === 0 && end === last ? null : { start, end };
}

/** Sets the kept range while reviewing; anything else is left untouched. */
export function setTrim(state: RecorderState, range: TrimRange | null): RecorderState {
  if (!canTrim(state)) return state;
  const next = normalizeTrim(state.points, range);
  const current = state.trim ?? null;
  if (next?.start === current?.start && next?.end === current?.end) return state;
  return { ...state, trim: next };
}

/**
 * The state as it would be saved with the current trim: points, distance,
 * start/end time, pauses and laps of the kept window. Without a trim it is
 * the same object, so the screen can memoize on it.
 */
export function trimmedView(state: RecorderState): RecorderState {
  const range = normalizeTrim(state.points, state.trim);
  if (range === null) return state;
  return { ...cutWindow(state, range), trim: range };
}

/**
 * Save pressed: the trim becomes final. The cut points are dropped from the
 * state itself, so a retry or a restore after a crash sends exactly the same
 * activity.
 */
export function confirmSave(state: RecorderState): RecorderState {
  if (state.status !== 'finished' || state.awaitingSave !== true) return state;
  const range = normalizeTrim(state.points, state.trim);
  if (range === null) return { ...state, awaitingSave: false, trim: null };
  return {
    ...cutWindow(state, range),
    awaitingSave: false,
    trim: null,
    trimmed: summarize(state, range),
  };
}

/** What the range removes from each end — for the slider labels and the diagnostics. */
export function summarize(state: RecorderState, range: TrimRange): TrimSummary {
  const points = state.points;
  const last = points.length - 1;
  const first = points[range.start] as TracePoint;
  const final = points[range.end] as TracePoint;
  return {
    originalPoints: points.length,
    removedStartPoints: range.start,
    removedEndPoints: last - range.end,
    removedStartM: Math.round(anchoredTotal(points.slice(0, range.start + 1)).distanceM),
    removedEndM: Math.round(anchoredTotal(points.slice(range.end)).distanceM),
    removedStartMs: range.start === 0 ? 0 : Math.max(0, first.t - (state.startedAt ?? first.t)),
    removedEndMs: range.end === last ? 0 : Math.max(0, (state.endedAt ?? final.t) - final.t),
  };
}

function cutWindow(state: RecorderState, range: TrimRange): RecorderState {
  const last = state.points.length - 1;
  const points = state.points.slice(range.start, range.end + 1);
  const first = points[0] as TracePoint;
  const final = points[points.length - 1] as TracePoint;
  // An untouched end keeps its original time: the recording really started
  // (or stopped) then, even if the first fix came a few seconds later.
  const startedAt = range.start === 0 ? (state.startedAt ?? first.t) : first.t;
  const endedAt = range.end === last ? (state.endedAt ?? final.t) : final.t;
  const { distanceM, anchor } = anchoredTotal(points);
  const pauseSpans = state.pauseSpans === undefined ? undefined : clipSpans(state.pauseSpans, startedAt, endedAt);
  const pausedMs = pauseSpans === undefined
    // Older save without spans: the pause total can only be capped by the window.
    ? Math.min(state.pausedMs, Math.max(0, endedAt - startedAt))
    : pauseSpans.reduce((sum, span) => sum + (span.to - span.from), 0);
  return {
    ...state,
    points,
    distanceM,
    anchor,
    startedAt,
    endedAt,
    pausedMs,
    ...(pauseSpans === undefined ? {} : { pauseSpans }),
    laps: cutLaps(state.laps, state.points, range.start, startedAt, endedAt),
  };
}

function clipSpans(spans: readonly PauseSpan[], from: number, to: number): PauseSpan[] {
  const clipped: PauseSpan[] = [];
  for (const span of spans) {
    const start = Math.max(span.from, from);
    const end = Math.min(span.to, to);
    if (end > start) clipped.push({ from: start, to: end });
  }
  return clipped;
}

/**
 * Laps inside the window, with distances counted from the new start. The
 * first lap always begins at the new start, so no stretch belongs to no lap.
 */
function cutLaps(
  laps: readonly LapMark[],
  points: readonly TracePoint[],
  startIndex: number,
  startedAt: number,
  endedAt: number,
): LapMark[] {
  const removedM = startIndex === 0 ? 0 : anchoredTotal(points.slice(0, startIndex + 1)).distanceM;
  const inside = laps
    .filter((lap) => lap.at > startedAt && lap.at < endedAt)
    .map((lap) => ({ at: lap.at, distanceM: Math.max(0, lap.distanceM - removedM) }));
  return [{ at: startedAt, distanceM: 0 }, ...inside];
}

function clampIndex(value: number, min: number, max: number): number {
  const rounded = Number.isFinite(value) ? Math.round(value) : min;
  return Math.min(max, Math.max(min, rounded));
}
